import type React from "react";
import { useTranslation } from "react-i18next";
import { MappingStatus } from "@models/job";
import type {
  MappingResultRow,
  MappingResultTarget,
} from "@models/mappingResult";
import { MAPPING_STATUS_BADGE } from "@utils/resultStatusBadges";
import StatusBadge from "./StatusBadge";

interface RowFindingBadgeProps {
  row: MappingResultRow;
  /** The target rendered beside this badge, when there is one. Only read to
   *  decide whether the finding is already said by the AI marker over it. */
  target?: MappingResultTarget | null;
}

/**
 * What the PIPELINE concluded about a row, as a badge — or nothing.
 *
 * Both tables render this under the suggestion it judges: technical-manual
 * under the catalogued value, wd-mapping under the suggested ItemMP text. A
 * finding is always a claim about one cell's contents, and in the Status column
 * it read as a claim about the row.
 *
 * It is a component rather than three lines repeated per table because those
 * three lines drifted: the size, the two translation keys and the "render
 * nothing when there is no finding" rule are one decision, and TM kept the old
 * badge size for a while after wd-mapping shrank it. Here the two tables
 * cannot disagree.
 *
 * `null` for `mapped` and `failed` — see `MAPPING_STATUS_BADGE` for why each
 * says nothing. A clean row shows the value alone.
 *
 * AND `null` for a `no_item_found` row whose name the AI proposed, because the
 * "AI Suggestion" marker over that name already says it: a proposal exists
 * precisely when the catalogue has no entry to point at. Two badges under one
 * name read as two findings. The badge stays for the miss the model did NOT
 * name — there the row shows the part's own OE text, which says nothing about
 * the catalogue at all, and dropping the badge would leave the reviewer with a
 * plain name and no finding.
 */
const RowFindingBadge: React.FC<RowFindingBadgeProps> = ({ row, target }) => {
  const { t } = useTranslation();
  const spec = MAPPING_STATUS_BADGE[row.mappingStatus];
  if (!spec) return null;
  if (
    row.mappingStatus === MappingStatus.NoItemFound &&
    target != null &&
    "isAiSuggestion" in target &&
    target.isAiSuggestion
  ) {
    return null;
  }

  return (
    <StatusBadge
      size="sm"
      spec={spec}
      text={t(spec.badgeKey, { defaultValue: spec.badge })}
      label={t(spec.labelKey, { defaultValue: spec.fallback })}
    />
  );
};

export default RowFindingBadge;
